import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Ergebnis eines Entra-Abgleichs (#116). Liegt als jsonb in `entra_user_load_einstellungen`.
 * `teilweise` = Lauf durchgelaufen, aber mit Fehlerzeilen (z. B. Einzelabfrage gescheitert,
 * Konflikt, Adresse freigegeben) oder ausgesetzten Deaktivierungen — vor dem Einschalten des
 * Zeitplans prüfen. Neue Felder haben Defaults, damit ältere gespeicherte Ergebnisse weiter parsen.
 */
export const EntraAbgleichErgebnisSchema = z.object({
  status: z.enum(['ok', 'teilweise', 'abgebrochen', 'nicht_konfiguriert']),
  /** Probelauf: alles berechnet (inkl. QM-Überführung), dann zurückgerollt — nichts gespeichert. */
  probelauf: z.boolean().default(false),
  geladen: z.number().int().nonnegative(),
  neu: z.number().int().nonnegative(),
  geaendert: z.number().int().nonnegative(),
  reaktiviert: z.number().int().nonnegative(),
  deaktiviert: z.number().int().nonnegative(),
  uebersprungen: z.object({
    gast: z.number().int().nonnegative(),
    extern: z.number().int().nonnegative(),
    ohne_mail: z.number().int().nonnegative(),
    ohne_personenname: z.number().int().nonnegative(),
    deaktiviert_in_entra: z.number().int().nonnegative(),
    testnutzer: z.number().int().nonnegative(),
  }),
  qm_zugeordnet: z.number().int().nonnegative(),
  qm_entfernt: z.number().int().nonnegative(),
  fehler: z.array(z.string()),
  /** Namen für die Admin-Anzeige. Bewusst hier und nicht im Log (PII). */
  namen: z.object({
    neu: z.array(z.string()),
    reaktiviert: z.array(z.string()),
    deaktiviert: z.array(z.string()),
    /** Wären deaktiviert worden, aber die Sicherung (mehr als das Maximum) hat ausgesetzt. */
    deaktivierung_ausgesetzt: z.array(z.string()).default([]),
  }),
});
export type EntraAbgleichErgebnis = z.infer<typeof EntraAbgleichErgebnisSchema>;

/** Body von `POST /v1/admin/neue-nutzer/abgleich/lauf`. Ohne Body = echter Lauf. */
export const EntraAbgleichLaufInputSchema = z.object({
  probelauf: z.boolean().default(false),
});
export type EntraAbgleichLaufInput = z.input<typeof EntraAbgleichLaufInputSchema>;

const UhrzeitSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const WochentagSchema = z.number().int().min(0).max(6);

export const UpdateEntraAbgleichZeitplanInputSchema = z.object({
  enabled: z.boolean(),
  time: UhrzeitSchema,
  weekdays: z.array(WochentagSchema).min(1),
});
export type UpdateEntraAbgleichZeitplanInput = z.infer<
  typeof UpdateEntraAbgleichZeitplanInputSchema
>;

export const EntraAbgleichEinstellungenSchema = z.object({
  enabled: z.boolean(),
  time: UhrzeitSchema,
  weekdays: z.array(WochentagSchema),
  timezone: z.string(),
  /** false = Gruppen-IDs oder AZURE_*-Werte fehlen → Lauf nicht möglich. */
  konfiguriert: z.boolean(),
  /** Sicherung: mehr Deaktivierungen als das → keine (ENTRA_USER_LOAD_MAX_DEAKTIVIERUNGEN). */
  max_deaktivierungen: z.number().int().nonnegative(),
  lauf_laeuft_seit: IsoDateTimeSchema.nullable(),
  letzter_lauf_start: IsoDateTimeSchema.nullable(),
  letzter_lauf_ende: IsoDateTimeSchema.nullable(),
  letzter_lauf_ergebnis: EntraAbgleichErgebnisSchema.nullable(),
});
export type EntraAbgleichEinstellungen = z.infer<typeof EntraAbgleichEinstellungenSchema>;

/** Warum ein Eintrag in „Neue Nutzer" erledigt ist. Wird berechnet, nie gespeichert. */
export const NeuerNutzerErledigtGrundSchema = z.enum(['geprueft', 'rolle', 'pl']);
export type NeuerNutzerErledigtGrund = z.infer<typeof NeuerNutzerErledigtGrundSchema>;

export const NeuerNutzerSchema = z.object({
  user_id: UuidSchema,
  name: z.string(),
  email: z.string(),
  active: z.boolean(),
  neu_seit: IsoDateTimeSchema,
  neu_anlass: z.enum(['neu', 'reaktiviert']),
  neu_geprueft_am: IsoDateTimeSchema.nullable(),
  neu_geprueft_von_name: z.string().nullable(),
  is_admin: z.boolean(),
  /** PL auf einem aktiven, nicht zusammengeführten Projekt — unabhängig von `erledigt_grund`. */
  ist_pl: z.boolean(),
  function_roles: z.array(
    z.object({
      user_function_role_id: UuidSchema,
      role: z.string(),
      scope_segment: z.string().nullable(),
    }),
  ),
  erledigt_grund: NeuerNutzerErledigtGrundSchema.nullable(),
});
export type NeuerNutzer = z.infer<typeof NeuerNutzerSchema>;
